import { Result, createErrorResult } from '../types/result'
import { Logger } from '../logger'
import * as fakeApi from './fakeApi'
import * as realApi from './realApi'
import { trackAppleSignup, trackGoogleSignup } from '../analytics/events'

interface LoggerAware {
  logger: Logger
}

type Api = typeof realApi | typeof fakeApi

const realApiInDevelopment: Record<string, Api> = {
  true: realApi,
  false: fakeApi,
  undefined: realApi,
}

const perEnvApis: Record<typeof process.env.NODE_ENV, Api> = {
  test: fakeApi,
  development:
    realApiInDevelopment[
      String(process.env.REACT_APP_USE_REAL_API_IN_DEVELOPMENT)
    ],
  production: realApi,
}

const api = perEnvApis[process.env.NODE_ENV]

export enum ValidateSignUpEmailError {
  Invalid = 'Email is invalid',
  Used = 'Email is used',
  Rejected = 'Email was rejected for unknown reasons',
  Uncertain = 'Email is not known to be valid',
}

export interface ValidateSignUpEmail {
  (email: string): Promise<Result<ValidateSignUpEmailError[]>>
}

export const makeValidateSignUpEmail =
  ({ logger }: LoggerAware): ValidateSignUpEmail =>
  async (...args) => {
    try {
      return await api.validateSignUpEmail(...args)
    } catch (e) {
      logger.captureException(e)
      return createErrorResult([ValidateSignUpEmailError.Uncertain])
    }
  }

export enum ValidateCodeError {
  Mismatch = 'Code did not match',
  Invalid = 'Code is not valid',
  TooManyAttempts = 'Login failed due to too many failed attempts in a row',
  Rejected = 'Code was rejected for unknown reasons',
  Uncertain = 'Code is not known to be valid',
}

export interface ValidateCode {
  (
    email: string,
    code: string,
    actionName: string,
    cookieDomain: string
  ): Promise<Result<ValidateCodeError[]>>
}

export const makeValidateCode =
  ({ logger }: LoggerAware): ValidateCode =>
  async (...args) => {
    try {
      return await api.validateCode(...args)
    } catch (e) {
      logger.captureException(e)
      return createErrorResult([ValidateCodeError.Uncertain])
    }
  }

export enum ResendCodeError {
  Uncertain = 'Code is not known to have been re-sent correctly',
}

export interface ResendCode {
  (email: string): Promise<Result<ResendCodeError[]>>
}

export const makeResendCode =
  ({ logger }: LoggerAware): ResendCode =>
  async (...args) => {
    try {
      return await api.resendCode(...args)
    } catch (e) {
      logger.captureException(e)
      return createErrorResult([ResendCodeError.Uncertain])
    }
  }

export enum LoginError {
  Uncertain = 'Login info is not known to be valid',
  Rejected = 'Login info was rejected for unknown reasons',
  InvalidEmail = 'Email is not valid',
  EmailLacksPassword = 'Email does not have a password',
  UnusedEmail = 'Email is not associated to an active account',
  InvalidCredentials = 'Login credentials are incorrect',
  TooManyAttempts = 'Login failed due to too many failed attempts in a row',
  PasswordLeaked = 'The password is known to have been leaked',
  BlockedUser = 'The user is blocked',
}

export interface Login {
  (
    email: string,
    password: string,
    cookieDomain: string
  ): Promise<Result<LoginError[]>>
}

export interface SocialLogin {
  (input: {
    userFlow: realApi.UserFlow
    connection: string
    cookieDomain: string
  }): void
}

export const makeLogin =
  ({ logger }: LoggerAware): Login =>
  async (...args) => {
    try {
      return await api.login(...args)
    } catch (e) {
      logger.captureException(e)
      return createErrorResult([LoginError.Uncertain])
    }
  }

export const makeGoogleLogin =
  ({
    dataLayer,
    logger,
    userFlow,
    cookieDomain,
  }: {
    dataLayer: (record: Record<string, unknown>) => void
    logger: Logger
    userFlow: realApi.UserFlow
    cookieDomain: string
  }): (() => void) =>
  () => {
    try {
      dataLayer(trackGoogleSignup())
      return api.authorize({
        userFlow,
        connection: 'google-oauth2',
        cookieDomain,
      })
    } catch (e) {
      logger.captureException(e)
      throw e
    }
  }

export const makeAppleLogin =
  ({
    dataLayer,
    logger,
    userFlow,
    cookieDomain,
  }: {
    dataLayer: (record: Record<string, unknown>) => void
    logger: Logger
    userFlow: realApi.UserFlow
    cookieDomain: string
  }): (() => void) =>
  () => {
    try {
      dataLayer(trackAppleSignup())
      return api.authorize({ userFlow, connection: 'apple', cookieDomain })
    } catch (e) {
      logger.captureException(e)
      throw e
    }
  }

export enum CreateNewPasswordError {
  Invalid = 'Email is invalid',
  Uncertain = 'Email is not known to be valid',
  Rejected = 'Email was rejected for unknown reasons',
  UnusedEmail = 'Email is not associated to an active account',
  EmailHasPassword = 'Email already has a password',
}

export interface CreateNewPassword {
  (email: string): Promise<Result<CreateNewPasswordError[]>>
}

export const makeCreateNewPassword =
  ({ logger }: LoggerAware): CreateNewPassword =>
  async (...args) => {
    try {
      return await api.createNewPassword(...args)
    } catch (e) {
      logger.captureException(e)
      return createErrorResult([CreateNewPasswordError.Uncertain])
    }
  }
